import { useDraggable, useDroppable } from '@dnd-kit/core';
import { Box, Tooltip } from '@mui/material';
import {
  CSSProperties, FC, KeyboardEvent, memo, MouseEvent, useCallback, useMemo,
} from 'react';
import { DetailPanelClickRect } from 'src/components/dispatch/detailPanel/DetailPanelShell';
import { formatDisplayDayLabel } from 'src/pages/Map/displayDays';

import { buildDropTargets, businessDayOf, DropTarget } from './buildDropTargets';
import { buildLaneBoxLayout, TripBarLayout } from './buildLaneBoxLayout';
import { longHaulTripSummary } from './dragOverlay';
import {
  LONG_HAUL_DAY_BOX_HEIGHT, LONG_HAUL_DAY_HEADER_GAP, LONG_HAUL_HEADER_HEIGHT,
  LONG_HAUL_WEEKDAY_ROW_HEIGHT,
} from './layout';
import { Lane, LongHaulTrip } from './types';

/**
 * レーンの上端からバーまでの余白。
 *
 * サンプル（MultiDayBoard.tsx:704, 835-838）は `top: 8`・
 * `height: (rowHeight - 16) / 1 - 24` で、行 116px なら高さ 76px・下余白 32px になる。
 * 行の高さを同じ 116px にしたので（`layout.ts`）、上 8px と下 32px をそのまま写せば
 * バーの高さもサンプルと一致する。
 */
const BAR_TOP = 8;
/** 下端に残す余白。レーンの区切り線にバーを接触させないぶん */
const BAR_BOTTOM = 32;
/**
 * 段組みするレーンの下余白。サンプル（MultiDayBoard.tsx:705）の `laneH - 16` と同じく
 * 上と同じ幅にする。段が 2 つあるときに 32px も残すと 1 段あたり 38px しか取れない。
 */
const STACK_BOTTOM = BAR_TOP;
/** 段どうしの隙間（サンプル MultiDayBoard.tsx:838 の `slotH - 3`） */
const STACK_GAP = 3;
/**
 * 3 行（13 / 12 / 12px・行間 1.4）と上下の余白が収まる高さ。これを下回るレーンでは
 * サンプル（MultiDayBoard.tsx:705 の `dense`）と同じく文字を 1 段小さくする。
 */
const BAR_DENSE_HEIGHT = 57;

/**
 * 勤務時間帯の帯（`long-haul-worked-hours`）。レーンの最下端へ細く置く。
 *
 * Why not バーと同じ高さで敷く: 行の高さいっぱいに塗ると盤面が灰色で埋まり、
 * サンプルの白い見た目から離れる。伝えたいのは「その日のどの時間帯に働くか」だけなので、
 * 高さは要らない。
 *
 * Why 最下端: 行の区切り線に接していないと、上下どちらの行の勤務なのかが読み取りにくい
 * （PM 判断 2026-10-07）。
 */
const WORKED_HOURS_HEIGHT = 6;

/** バー左端の色帯の幅 */
const COLOR_BAND_WIDTH = 4;

/** 日付ヘッダの上段に出す曜日 */
const JP_WEEKDAY = ['日', '月', '火', '水', '木', '金', '土'] as const;

/** '2026-06-06' → '6/6'。バッジは月日だけ出す */
const monthDayLabel = (day: string): string => {
  const matched = /^\d{4}-(\d{2})-(\d{2})$/.exec(day);
  if (!matched) return day;
  return `${Number(matched[1])}/${Number(matched[2])}`;
};

/**
 * '2026-06-06' → 曜日。読めない日は空文字。
 *
 * Why not `new Date(day)`: ISO の日付だけの文字列は UTC として解釈されるので、JST では
 * 前日の曜日になる。年月日を数値で渡してローカルの壁時計日付にすると、どの TZ でも
 * 同じ曜日が出る（曜日は日付だけで決まり時刻を持たないため）。
 */
const weekdayLabel = (day: string): string => {
  const matched = /^(\d{4})-(\d{2})-(\d{2})$/.exec(day);
  if (!matched) return '';
  const date = new Date(Number(matched[1]), Number(matched[2]) - 1, Number(matched[3]));
  return JP_WEEKDAY[date.getDay()] ?? '';
};

/** 曜日の色。土は青・日は赤・平日はグレー（サンプル MultiDayBoard.tsx の日付ヘッダ） */
const weekdayColor = (day: string): string => {
  const label = weekdayLabel(day);
  if (label === '日') return 'hsl(var(--destructive))';
  if (label === '土') return 'hsl(var(--primary))';
  return 'hsl(var(--muted-foreground))';
};

/**
 * 勤務 1 件ぶんのドロップ受け口。
 *
 * Why not レーン側でまとめて `useDroppable` を呼ぶ: フックなので 1 ドロップ先 = 1 回の
 * 呼び出しになる。`isOver` を勤務ごとに出し分けるにはコンポーネントに切り出すしかない。
 */
const WorkDropTarget: FC<{
  target: DropTarget;
  left: number;
  width: number;
  height: number;
}> = ({ target, left, width, height }) => {
  const { setNodeRef, isOver } = useDroppable({
    id: target.droppableId,
    data: { shiftTruckId: target.shiftTruckId, targetRowId: target.targetRowId },
  });

  return (
    <Box
      ref={setNodeRef}
      data-testid="long-haul-drop-target"
      data-over={isOver ? '' : undefined}
      style={{ left, width, top: BAR_TOP, height }}
      sx={{
        position: 'absolute',
        // 当たり判定は dnd-kit が rect で見るのでイベントは要らない。
        // 拾ってしまうと下の日セル（休み設定）が押せなくなる
        pointerEvents: 'none',
        boxSizing: 'border-box',
        borderRadius: 1,
        /*
          受け口であることは破線で示す（サンプル MultiDayBoard.tsx:138-144）。
          offset を内側（負）にして、隣の帯の上へ 2px はみ出さないようにする。

          Why not border: 実線の border はバーの影やレーンの区切り線と同じ描線になり、
          「枠が付いた」のか「線が 1 本増えた」のか見分けがつかない。outline なら
          box-sizing に関係なく箱の外周へ重なるので、帯の幅計算にも影響しない。
        */
        outline: isOver ? '2px dashed hsl(var(--primary))' : 'none',
        outlineOffset: '-3px',
        /*
          ドラッグ中でなければ塗らない（サンプル MultiDayBoard.tsx:137-144）。

          Why not 勤務のある日を灰色に塗る: サンプルは灰色を「休み」に使っている。
          こちらが「勤務がある」に使うと意味が逆になり、読み違える。勤務があることは
          実際に働く時間帯の重ね（`long-haul-worked-hours`）で示す。
        */
        bgcolor: isOver ? 'hsl(var(--primary)/0.10)' : 'transparent',
        transition: 'background-color 120ms',
      }}
    />
  );
};

/**
 * 運行 1 本のバー。
 *
 * 出すのは 荷主 / 積地 → 降地 / 都道府県区間。ドライバーはレーン単位で決まり
 * 左カラムに出るので、ここには出さない。
 */
const TripBar: FC<{
  layout: TripBarLayout;
  top: number;
  height: number;
  /** 段が低く 3 行が入りきらないレーン。文字を 1 段小さくしてホバーで全文を出す */
  dense: boolean;
  /** 押したバーの矩形も渡す。詳細パネルを押した位置へ寄せるのに使う */
  onSelect: (trip: LongHaulTrip, clickRect: DetailPanelClickRect) => void;
  /**
   * セルの色分けで決まった色。null なら色分けが効いていない（または区分が引けない）。
   * どの区分から引いた色かはここでは扱わない（判定は `resolveCellColor`）。
   */
  color: string | null;
}> = ({
  layout, top, height, dense, onSelect, color,
}) => {
  const { trip, left, width, startsBeforeLabel } = layout;
  const {
    attributes, listeners, setNodeRef, transform, isDragging,
  } = useDraggable({ id: trip.barId, data: { longHaulTrip: trip } });

  const style: CSSProperties = useMemo(() => ({
    left,
    width,
    top,
    height,
    transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined,
    opacity: isDragging ? 0.5 : 1,
  }), [left, width, top, height, transform, isDragging]);

  const handleClick = useCallback((e: MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    onSelect(trip, e.currentTarget.getBoundingClientRect());
  }, [onSelect, trip]);

  // キーボードでも位置を渡す。フォーカスしているバー自身が押した位置にあたる
  const handleKeyDown = useCallback((e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'Enter' && e.key !== ' ') return;
    e.preventDefault();
    onSelect(trip, e.currentTarget.getBoundingClientRect());
  }, [onSelect, trip]);

  const route = [trip.loadingName, ...trip.unloadingNames].filter(Boolean).join(' → ');
  /**
   * 複数箇所降ろしのバッジ。2 箇所は `(2)`、3 箇所以上は `[3]`（サンプル MultiDayBoard.tsx）。
   * 括弧の形を変えるのは、幅の狭いバーで地点名が省略されても「何箇所降ろすか」が
   * 数字だけでなく形でも読めるようにするため。
   */
  const dropCountLabel = trip.unloadingNames.length > 1
    ? (trip.unloadingNames.length === 2 ? '(2)' : `[${trip.unloadingNames.length}]`)
    : null;
  const prefectures = trip.loadingPrefecture && trip.unloadingPrefectures.length > 0
    ? [trip.loadingPrefecture, ...trip.unloadingPrefectures].join(' → ')
    : null;

  // 掴んでいる間のオーバーレイと同じ文言にする（`longHaulTripSummary`）
  const summary = longHaulTripSummary(trip);

  const bar = (
    <Box
      ref={setNodeRef}
      data-testid="long-haul-trip-bar"
      style={style}
      {...attributes}
      {...listeners}
      role="button"
      tabIndex={0}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      sx={{
        position: 'absolute',
        zIndex: 2,
        boxSizing: 'border-box',
        overflow: 'hidden',
        // 左端の色帯（COLOR_BAND_WIDTH）と文字が重ならないぶんだけ左を空ける
        pl: `${COLOR_BAND_WIDTH + 6}px`,
        pr: 0.75,
        py: dense ? 0 : 0.25,
        borderRadius: 1.5,
        cursor: 'grab',
        userSelect: 'none',
        // 色分けが効いていないときは今までどおりのカード色。効いているときだけ
        // 区分の色を薄く敷く（12% はサンプル MultiDayBoard.tsx:845 と同じ割合）
        bgcolor: color ? `color-mix(in srgb, ${color} 12%, hsl(var(--card)))` : 'hsl(var(--card))',
        boxShadow: '0 0 0 1px hsl(var(--border)), 0 1px 3px hsl(var(--foreground)/0.08)',
        transition: 'transform 120ms, box-shadow 120ms',
        '&:active': { cursor: 'grabbing' },
        '&:hover': {
          transform: 'translateY(-1px)',
          boxShadow: '0 0 0 1px hsl(var(--border)), 0 4px 12px hsl(var(--foreground)/0.16)',
        },
      }}
    >
      <Box
        data-testid="long-haul-trip-bar-color"
        sx={{
          position: 'absolute',
          left: 0,
          top: 0,
          bottom: 0,
          width: `${COLOR_BAND_WIDTH}px`,
          bgcolor: color ?? 'hsl(var(--foreground))',
        }}
      />
      <Box
        sx={{
          display: 'flex', alignItems: 'baseline', gap: 0.5, minWidth: 0,
        }}
      >
        {/* 黒地・白抜き（サンプル MultiDayBoard.tsx:864-879）。荷主名と同じ 1 行に並ぶので、
            地を敷かないとバーの本文に埋もれて「いつ積んだか」が拾えない */}
        {startsBeforeLabel && (
          <Box
            component="span"
            data-testid="long-haul-trip-bar-starts-before"
            sx={{
              flexShrink: 0,
              px: 0.5,
              py: '1px',
              borderRadius: '4px',
              bgcolor: 'hsl(var(--foreground))',
              color: 'hsl(var(--background))',
              fontSize: dense ? 8 : 9,
              fontWeight: 700,
              lineHeight: 1.4,
              whiteSpace: 'nowrap',
            }}
          >
            {startsBeforeLabel}
          </Box>
        )}
        <Box
          component="span"
          sx={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            fontSize: dense ? 11 : 13,
            fontWeight: 700,
            color: 'hsl(var(--card-foreground))',
          }}
        >
          {trip.shipperName}
        </Box>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
        <Box
          sx={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            fontSize: dense ? 10 : 12,
            fontWeight: 600,
            lineHeight: 1.4,
            color: 'hsl(var(--card-foreground))',
          }}
        >
          {route}
        </Box>
        {dropCountLabel && (
          <Box
            component="span"
            data-testid="long-haul-trip-drop-count"
            sx={{
              flexShrink: 0,
              px: 0.5,
              borderRadius: 0.5,
              border: '1px solid hsl(var(--card-foreground))',
              fontSize: dense ? 8 : 9,
              fontWeight: 700,
              lineHeight: 1.4,
              color: 'hsl(var(--card-foreground))',
            }}
          >
            {dropCountLabel}
          </Box>
        )}
      </Box>
      {prefectures && (
        <Box
          sx={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            fontSize: dense ? 10 : 12,
            fontWeight: 600,
            lineHeight: 1.4,
            /*
              荷主名・積地→降地と同じ色にする（サンプル MultiDayBoard.tsx:911、
              ユーザー判断 2026-09-29）。ここだけ薄くすると、同じバーの中で 3 行目が
              補足のように見える。
              Why not サンプルどおり `--foreground`: バーはカードなので、上 2 行と同じ
              `--card-foreground` を使う。値は同じで、1 枚のカードの中で token が割れない
            */
            color: 'hsl(var(--card-foreground))',
          }}
        >
          {prefectures}
        </Box>
      )}
    </Box>
  );

  // 段組みで切れた行を拾えるようにする。段組みしないレーンでは全文が見えているので出さない
  return dense ? <Tooltip title={summary}>{bar}</Tooltip> : bar;
};

type BoardLaneProps = {
  lane: Lane;
  days: readonly string[];
  rowHeight: number;
  dayColWidth: number;
  onSelectTrip: (trip: LongHaulTrip, clickRect: DetailPanelClickRect) => void;
  onSelectDayCell: (lane: Lane, day: string, anchorEl: HTMLElement) => void;
  cellColorOf?: (trip: LongHaulTrip) => string | null;
};

const BoardLane: FC<BoardLaneProps> = ({
  lane, days, rowHeight, dayColWidth, onSelectTrip, onSelectDayCell, cellColorOf,
}) => {
  const dropTargets = useMemo(() => buildDropTargets(lane, days), [lane, days]);

  // 勤務が覆う日だけセルを押せる。休み（勤務が無い日）はポップオーバーを開かない
  const coveredDayIndexes = useMemo(() => {
    const covered = new Set<number>();
    dropTargets.forEach((target) => {
      for (let i = target.startDayIndex; i <= target.endDayIndex; i += 1) covered.add(i);
    });
    return covered;
  }, [dropTargets]);

  const { bars, stackCount, bands } = useMemo(
    () => buildLaneBoxLayout(lane, days, dayColWidth, dropTargets),
    [lane, days, dayColWidth, dropTargets],
  );

  const handleDayCellClick = useCallback((e: MouseEvent<HTMLDivElement>) => {
    const index = Number(e.currentTarget.dataset.dayIndex);
    if (!coveredDayIndexes.has(index)) return;
    onSelectDayCell(lane, days[index], e.currentTarget);
  }, [coveredDayIndexes, onSelectDayCell, lane, days]);

  /**
   * バーを置ける縦幅。段組みするレーンは下余白を詰めて段の高さを稼ぐ
   * （詰めないと 1 段 38px で 3 行が 1 行ぶんしか入らない）。
   */
  const stackedBottom = stackCount > 1 ? STACK_BOTTOM : BAR_BOTTOM;
  const stackArea = Math.max(1, rowHeight - BAR_TOP - stackedBottom);
  const slotHeight = stackArea / stackCount;
  const barHeight = Math.max(1, slotHeight - (stackCount > 1 ? STACK_GAP : 0));
  /** 勤務時間帯の帯はレーンの最下端（区切り線に接する位置）へ置く */
  const workedTop = rowHeight - WORKED_HOURS_HEIGHT;

  return (
    <Box
      data-testid="long-haul-lane"
      style={{ height: rowHeight, width: days.length * dayColWidth }}
      sx={{ position: 'relative', borderBottom: '1px solid hsl(var(--border))' }}
    >
      {/* 日の区切り線。サンプル（MultiDayBoard.tsx:670-684）と同じく専用のレイヤーで引く。
          いちばん下に敷くので、勤務帯や重ねが載っても線が消えない。
          Why not 日セルの borderRight: 日セルは休み設定の当たり判定も兼ねていて、
          上に何か載るたびに線が隠れるかどうかが変わる */}
      {days.slice(1).map((day, index) => (
        <Box
          key={`${day}::divider`}
          data-testid="long-haul-day-divider"
          style={{ left: (index + 1) * dayColWidth }}
          sx={{
            position: 'absolute',
            top: 0,
            bottom: 0,
            width: 0,
            pointerEvents: 'none',
            borderLeft: '1px solid hsl(var(--border))',
          }}
        />
      ))}

      {days.map((day, index) => (
        <Box
          key={day}
          data-testid="long-haul-day-cell"
          data-day-index={index}
          role="button"
          tabIndex={-1}
          aria-label={`${formatDisplayDayLabel(day)} の勤務`}
          onClick={handleDayCellClick}
          style={{ left: index * dayColWidth, width: dayColWidth }}
          sx={{
            position: 'absolute',
            top: 0,
            bottom: 0,
            boxSizing: 'border-box',
            cursor: coveredDayIndexes.has(index) ? 'pointer' : 'default',
          }}
        />
      ))}

      {bands.map((band) => (
        <WorkDropTarget
          key={band.target.droppableId}
          target={band.target}
          left={band.left}
          width={band.width}
          height={stackArea}
        />
      ))}

      {/* 実際に働く時間帯。帯（ドロップ先）は日単位なので、午前休みで勤務が縮んでも動かない。
          時刻に比例した位置をこちらで重ねて、休みを入れた結果を読めるようにする。
          当たり判定は帯が持つので、こちらはイベントを拾わない */}
      {bands.map((band) => band.worked && (
        <Box
          key={`${band.target.droppableId}::worked`}
          data-testid="long-haul-worked-hours"
          style={{
            left: band.worked.left,
            width: band.worked.width,
            top: workedTop,
            height: WORKED_HOURS_HEIGHT,
          }}
          sx={{
            position: 'absolute',
            pointerEvents: 'none',
            boxSizing: 'border-box',
            borderRadius: 1,
            bgcolor: 'hsl(var(--muted-foreground)/0.16)',
          }}
        />
      ))}

      {bars.map((layout) => (
        <TripBar
          key={layout.trip.barId}
          layout={layout}
          top={BAR_TOP + layout.stackSlot * slotHeight}
          height={barHeight}
          dense={barHeight < BAR_DENSE_HEIGHT}
          onSelect={onSelectTrip}
          color={cellColorOf?.(layout.trip) ?? null}
        />
      ))}

    </Box>
  );
};

export type LongHaulBoardProps = {
  lanes: Lane[];
  days: readonly string[];
  rowHeight: number;
  dayColWidth: number;
  onSelectTrip: (trip: LongHaulTrip, clickRect: DetailPanelClickRect) => void;
  /** 勤務がある日のセルを押したとき。休み設定のポップオーバーを開く位置に anchorEl を渡す */
  onSelectDayCell: (lane: Lane, day: string, anchorEl: HTMLElement) => void;
  /**
   * 運行 1 本に塗る色（セルの色分け #338 C2）。未指定・null なら今までどおりの黒。
   * 判定そのものは `resolveCellColor` が持つので、ボードは色文字列しか知らない。
   */
  cellColorOf?: (trip: LongHaulTrip) => string | null;
};

/**
 * 中央ボード。横軸 = 日付、縦軸 = レーン。1 運行 = 積日から卸日まで伸びる 1 本のバー。
 *
 * ドロップ先は日セルではなく勤務単位（`buildDropTargets`）。保存経路が運ぶのは
 * shift_truck_id / order_id / action / cycle だけで日は入力にならないため、日セルへ
 * 分けると「どの日に落としても同じ結果」の操作を画面へ出すことになる。
 */
const LongHaulBoard: FC<LongHaulBoardProps> = ({
  lanes, days, rowHeight, dayColWidth, onSelectTrip, onSelectDayCell, cellColorOf,
}) => {
  /**
   * 当日。判定は JST 固定（`businessDayOf`）で、ブラウザ TZ では切らない。
   * UTC 環境だと JST の 0 時〜9 時が前日扱いになり、強調が 1 日ずれる。
   */
  const today = useMemo(() => businessDayOf(new Date().toISOString()), []);

  return (
    /*
      スクロールは縦横とも持たない。中身なりに伸びて、外側の 1 つの器（左カラムと共有）に
      任せる。カード型配車の中央カラム（`board-column`）と同じ形。

      Why not overflowX: 'auto': CSS では片方向だけスクロールする要素は作れない。x を auto に
      すると y も visible から auto へ格上げされ、中央だけ内側に縦スクロールができて左カラムと
      縦位置がずれる（実測 76px ずれていた）。日付ヘッダの sticky top も、その内側スクロールを
      基準にしてしまうので外側のスクロールでは流れて消える。
    */
    <Box
      data-testid="long-haul-board"
      sx={{
        minWidth: 'max-content',
        /*
          盤面の地色。祖先はどれも背景を持たず、body が灰色（rgb(240,240,240)）なので、
          ここで塗らないと勤務帯の灰色を消した瞬間に盤面へ地色が透ける。
          サンプルも中央ボードの根に白を置いている（MultiDayBoard.tsx:573）。
          ヘッダと同じトークンを使い、ヘッダと本体で地色が割れないようにする。
        */
        bgcolor: 'hsl(var(--background))',
      }}
    >
      {/* 高さは左カラムのヘッダと共有する。揃えないとレーンと車両行が縦にずれる */}
      <Box
        data-testid="long-haul-board-header"
        style={{ height: LONG_HAUL_HEADER_HEIGHT }}
        sx={{
          boxSizing: 'border-box',
          display: 'flex',
          alignItems: 'center',
          position: 'sticky',
          top: 0,
          zIndex: 3,
          bgcolor: 'hsl(var(--background))',
          borderBottom: '1px solid hsl(var(--border))',
        }}
      >
        {days.map((day) => {
          const isToday = today != null && day === today;
          return (
            <Box
              key={day}
              data-testid="long-haul-day-header"
              style={{ width: dayColWidth }}
              sx={{
                boxSizing: 'border-box',
                flexShrink: 0,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: `${LONG_HAUL_DAY_HEADER_GAP}px`,
                px: 1,
                borderRight: '1px solid hsl(var(--border)/0.6)',
              }}
            >
              <Box
                component="span"
                data-testid="long-haul-day-weekday"
                sx={{
                  height: `${LONG_HAUL_WEEKDAY_ROW_HEIGHT}px`,
                  lineHeight: `${LONG_HAUL_WEEKDAY_ROW_HEIGHT}px`,
                  fontSize: 11,
                  fontWeight: 600,
                  color: weekdayColor(day),
                }}
              >
                {weekdayLabel(day)}
              </Box>
              <Box
                component="span"
                data-testid="long-haul-day-date"
                data-today={isToday ? '' : undefined}
                sx={{
                  boxSizing: 'border-box',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: `${LONG_HAUL_DAY_BOX_HEIGHT}px`,
                  minWidth: 64,
                  px: 1,
                  borderRadius: 1.5,
                  // 個別プロパティで書く。shorthand に CSS 変数を混ぜると
                  // 枠色だけを確かめるテストが値を取れない
                  borderWidth: isToday ? 1.5 : 1,
                  borderStyle: 'solid',
                  borderColor: isToday ? 'hsl(var(--primary))' : 'hsl(var(--border))',
                  bgcolor: isToday ? 'hsl(var(--primary) / 0.1)' : 'transparent',
                  fontSize: 13,
                  fontWeight: isToday ? 700 : 600,
                  color: 'hsl(var(--foreground))',
                }}
              >
                {monthDayLabel(day)}
              </Box>
            </Box>
          );
        })}
      </Box>

      {/* レーンが 1 本も無いとき。ヘッダだけ残して本文が真っ白になると、読み込み中なのか
          車両が 1 台も無いのか画面から区別できない（サンプル MultiDayBoard.tsx:647-651）。
          幅を日列の合計に合わせて、日付ヘッダの真下で中央に出す */}
      {lanes.length === 0 && (
        <Box
          data-testid="long-haul-board-empty"
          style={{ width: days.length * dayColWidth }}
          sx={{
            py: 6, textAlign: 'center', color: 'text.disabled', fontSize: 12,
          }}
        >
          車両がありません
        </Box>
      )}

      {lanes.map((lane) => (
        <BoardLane
          key={lane.laneId}
          lane={lane}
          days={days}
          rowHeight={rowHeight}
          dayColWidth={dayColWidth}
          onSelectTrip={onSelectTrip}
          onSelectDayCell={onSelectDayCell}
          cellColorOf={cellColorOf}
        />
      ))}
    </Box>
  );
};

export default memo(LongHaulBoard);
